import { cx } from '@vizit/theme';
import { getScoreColorVariant, serializeScore } from '@vizit/utils';
import { ScoreDiffIndicator } from '../score-diff-indicator';
import { Skeleton } from '../skeleton';
import { Text, TextColor, TextVariant } from '../text';
import { cardClasses, labelClasses, scoreTextClasses, skeletonClasses } from './styles';
import { type ScoreStickerCardProps, ScoreStickerCardSize } from './types';

/**
 * A card-sized score sticker: a square tinted by the score's tier, carrying a caption and a
 * large headline score, with an optional diff beneath it.
 *
 * Sizes: Lg (187px, default) is the prominent state; Md (155px) sits level with
 * the product page metric cells. Changing `size` animates between the two.
 *
 * @example
 * <ScoreStickerCard label="PRODUCT PAGE SCORE" score={23} scoreDiff={11} />
 */
function ScoreStickerCard(props: ScoreStickerCardProps) {
  const {
    label,
    score,
    scoreDiff,
    size = ScoreStickerCardSize.Lg,
    isLoading = false,
    isMuted = false,
    isDisabled = false,
    className,
    ref,
    ...other
  } = props;

  const scoreLabel = serializeScore(score);
  const colorVariant = getScoreColorVariant(score);
  const labelClassName = labelClasses({ colorVariant, isMuted, isDisabled });

  if (isLoading) {
    return (
      <div
        ref={ref}
        className={cx(skeletonClasses({ size }), className)}
        aria-label={`${label}: loading`}
        aria-busy="true"
        data-testid="score-sticker-card"
        data-size={size}
        {...other}
      >
        <Skeleton variant="square" className="h-5 w-32 self-start" />
        <Skeleton variant="square" className={size === ScoreStickerCardSize.Lg ? 'h-[60px] w-36' : 'h-[48px] w-28'} />
        <Skeleton variant="square" className="mt-2 h-3 w-12 self-start" />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cx(cardClasses({ size, colorVariant, isMuted, isDisabled }), className)}
      aria-label={`Score: ${scoreLabel}`}
      data-testid="score-sticker-card"
      data-size={size}
      data-muted={isMuted ? 'true' : 'false'}
      data-disabled={isDisabled ? 'true' : 'false'}
      {...other}
    >
      <Text variant={TextVariant.MonoXs} color={TextColor.Quaternary} showEllipsis={false} className={labelClassName}>
        {label}
      </Text>
      <span className={scoreTextClasses({ size, colorVariant, isMuted, isDisabled })} data-testid="score-value">
        {scoreLabel}
      </span>
      {scoreDiff != null && (
        <ScoreDiffIndicator scoreDiff={scoreDiff} className={cx('self-start mt-2', isDisabled && 'opacity-20')} />
      )}
    </div>
  );
}

export { ScoreStickerCard };
export { ScoreStickerCardSize } from './types';
export type { ScoreStickerCardProps };
